import { severityLevelFromString } from "@sentry/core";
import * as Sentry from "@sentry/react";

export function catchErrorLog(
  message: string,
  level: Sentry.SeverityLevel = severityLevelFromString("error")
): void {
  if (import.meta.env.VITE_APP_ENV === "development") {
    console.error(message);
    return;
  }

  // Skip 404 errors from being reported to Sentry
  if (
    message.includes("404") ||
    message.includes("Not Found") ||
    message.includes("not found")
  ) {
    console.warn("[Sentry] Skipped 404 error:", message);
    return;
  }

  // Skip 401 authentication errors from being reported to Sentry
  if (
    message.includes("401") ||
    message.includes("Unauthorized") ||
    message.includes("failed to authentication")
  ) {
    console.warn("[Sentry] Skipped 401 auth error:", message);
    return;
  }

  // Check if the message is a string
  const errorMessage = JSON.parse(JSON.stringify(message));
  Sentry.captureMessage(errorMessage, level);
}

/**
 * A failure that destroys a deliverable, reported no matter what it looks like.
 *
 * `catchErrorLog` above drops any message mentioning 401/404, and the global
 * `beforeSend` in `src/index.tsx` drops 404 again. That suppression is right for
 * the authenticated app, where those statuses are routine noise: a missing
 * record, an expired session the interceptor already handles.
 *
 * It is wrong for a headless render of a client-facing document. There a 401
 * (expired or forged render token) or a 404 (unknown assessment) is not noise —
 * it is the whole failure, and it is precisely the case nobody can diagnose
 * afterwards, because the only other symptom is a Playwright timeout 90 seconds
 * later with no clue why.
 *
 * Two rules for callers, both load-bearing:
 *
 *   • Keep status codes and raw error text OUT of `message`. The global
 *     `beforeSend` still inspects the message string, so "…status code 404"
 *     would be dropped here too. Put them in `context` — extras are not
 *     inspected (and avoid the key `status_code`, which `beforeSend` reads).
 *   • Keep `message` constant per failure mode, with the variable parts in
 *     `context`. Sentry groups by message, so a stable string turns "why did
 *     this client get no PDF?" into one issue with a countable history rather
 *     than a scatter of one-offs.
 *
 * Never pass a credential — tokens must not reach Sentry.
 */
/**
 * A request payload, safe to attach to a Sentry event.
 *
 * Returned as a STRING rather than the object itself, for two reasons that both
 * bite in practice:
 *
 *   • Sentry normalizes extras to a depth of 3, so a nested payload — a job's
 *     `hiring_flow.steps[].gates[]`, say — arrives as `[Object]` exactly where
 *     the interesting part was. A string survives intact.
 *   • Events over Sentry's size limit are truncated or dropped server-side, and
 *     a silently half-sent payload is worse than a clearly cut one. This caps
 *     the value and SAYS it was cut, so nobody reads a partial payload as the
 *     whole request.
 *
 * Circular references are tolerated rather than throwing — this runs inside a
 * catch block, and a logging helper must never become the reason a failure
 * handler fails.
 */
export function serializeRequestPayload(value: unknown, maxChars = 20_000): string {
  let json: string;
  try {
    const seen = new WeakSet<object>();
    json = JSON.stringify(value, (_key, val) => {
      if (typeof val === "object" && val !== null) {
        if (seen.has(val as object)) return "[circular]";
        seen.add(val as object);
      }
      return val;
    });
  } catch (error) {
    return `[unserializable payload: ${String(error)}]`;
  }
  if (json === undefined) return "[no payload]";
  return json.length > maxChars
    ? `${json.slice(0, maxChars)}… [truncated, ${json.length} chars total]`
    : json;
}

export function fatalErrorLog(
  message: string,
  context?: Record<string, unknown>,
  /** Indexed and filterable in Sentry — pass the few fields you will search by. */
  tags?: Record<string, string>
): void {
  if (import.meta.env.VITE_APP_ENV === "development") {
    console.error(message, context ?? {});
    return;
  }
  Sentry.captureMessage(message, {
    level: "fatal",
    tags,
    extra: context,
  });
}

export function setLogUser(
  userId: string,
  name?: string,
  email?: string
): void {
  if (import.meta.env.VITE_APP_ENV === "development") {
    console.info("User set:", { id: userId, username: name, email });
    return;
  }

  Sentry.setUser({ id: userId, username: name, email });
}
